import React, { useState, useEffect, createContext, useContext } from 'react';
import './NotificationSystem.css';

// Notification Context
const NotificationContext = createContext();

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      duration,
      timestamp: Date.now()
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }

    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  // Success notification shortcut
  const notifySuccess = (message, duration = 4000) => {
    return addNotification(message, 'success', duration);
  };

  // Error notification shortcut
  const notifyError = (message, duration = 6000) => {
    return addNotification(message, 'error', duration);
  };

  // Warning notification shortcut
  const notifyWarning = (message, duration = 5000) => {
    return addNotification(message, 'warning', duration);
  };

  // Info notification shortcut
  const notifyInfo = (message, duration = 4000) => {
    return addNotification(message, 'info', duration);
  };

  // Transaction notification shortcuts
  const notifyTransaction = {
    pending: (message = 'Transaction pending...') => {
      return addNotification(message, 'info', 0); // 0 = no auto remove
    },
    success: (message = 'Transaction successful!') => {
      return addNotification(message, 'success', 4000);
    },
    error: (message = 'Transaction failed!') => {
      return addNotification(message, 'error', 6000);
    }
  };

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,
    notifyTransaction
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

// Hook to use notifications
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Individual Notification Component
const NotificationItem = ({ notification, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => onRemove(notification.id), 300);
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
      default:
        return 'ℹ️';
    }
  };

  const getTypeClass = () => {
    switch (notification.type) {
      case 'success':
        return 'notification-success';
      case 'error':
        return 'notification-error';
      case 'warning':
        return 'notification-warning';
      case 'info':
      default:
        return 'notification-info';
    }
  };

  return (
    <div
      className={`notification-item ${getTypeClass()} ${isVisible ? 'notification-enter' : ''} ${isExiting ? 'notification-exit' : ''}`}
      onClick={handleRemove}
    >
      <div className="notification-icon">
        {getIcon()}
      </div>
      <div className="notification-content">
        <div className="notification-message">
          {notification.message}
        </div>
        <div className="notification-time">
          {new Date(notification.timestamp).toLocaleTimeString()}
        </div>
      </div>
      <button
        className="notification-close"
        onClick={(e) => {
          e.stopPropagation();
          handleRemove();
        }}
      >
        ×
      </button>
    </div>
  );
};

// Notification Container Component
const NotificationContainer = () => {
  const { notifications, removeNotification } = useNotification();

  return (
    <div className="notification-container">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
        />
      ))}
    </div>
  );
};

export default NotificationProvider;